import { useCallback, useMemo, useState } from "react";
import "../../css/GlobalStyles.css";
import "../../css/ExplorePage.css";
import { isDesktop } from "react-device-detect";
import { Icon } from "@iconify/react";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { Colors, addAlpha } from "../../utils/colors";
import {
  AccountData,
  Event,
  ThemeType,
  Visibility,
} from "@markit/common.types";
import {
  addSearchHistory,
  deleteSearchHistory,
  getAccountState,
} from "../../redux/slices/accountSlice";
import {
  ExploreTopCreators,
  ExploreTrendingCreators,
  fetchExploreFeaturedUsers,
} from "../../utils/ExploreUtils";
import { getEventData, getUserData } from "../../utils/FirebaseUtils";
import { algoliaClient } from "../../utils/algoliaUtils";
import TopHeader from "../../components/TopHeader";
import ExploreHero from "../../components/Explore/ExploreHero";
import FeaturedPeople from "../../components/Explore/FeaturedPeople";
import {
  FeaturedEventCardMobile,
  FeaturedEventCardMobileSearch,
} from "../../components/LandingPage/RenderedEvents";
import { EmptySearchState } from "../../components/EmptyStates/EmptySearchState";
import { RenderedUser } from "../../components/LandingPage/RenderedUsers";
import LoadingScreen from "../LoadingScreen";
import Footer from "../../components/Footer";
import useAsyncOnMount from "../../hooks/useAsyncEffectOnMount";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { Helmet } from "react-helmet";
import { MARKITAI } from "@markit/common.utils";
import { NavigationId } from "../../navigation/AppParamList";
import ExploreCategories from "../../components/Explore/ExploreCategories";
import { checkIsDesktop } from "../../utils/screenResponsivenessUtils";
import { useWindowResize } from "../../hooks/useWindowResize";

enum SearchOptionType {
  RECENT = "Recent",
  EVENTS = "Events",
  PEOPLE = "People",
}

enum Collection {
  USERS = "users",
  EVENTS = "events",
}

const ExplorePage = () => {
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const { searchHistory } = accountData;
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [featuredPeople, setFeaturedPeople] = useState<AccountData[]>([]);
  const [trendingPeople, setTrendingPeople] = useState<AccountData[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [displaySearchModal, setDisplaySearchModal] = useState<boolean>(false);
  const [selectedSearchOption, setSelectedSearchOption] =
    useState<SearchOptionType>(SearchOptionType.RECENT);
  const [searchedEventsHistory, setSearchedEventsHistory] = useState<Event[]>(
    []
  );
  const [searchedEvents, setSearchedEvents] = useState<Event[]>([]);
  const [searchedUsers, setSearchedUsers] = useState<AccountData[]>([]);

  const windowWidth = useWindowResize();

  const isDesktopSize = useMemo(
    () => checkIsDesktop(windowWidth),
    [windowWidth]
  );

  const styles = {
    gradientHeader: {
      backgroundColor: Colors.BLACK,
      height: isDesktop ? 500 : 350,
      width: "100%",
      minWidth: 0,
      alignItems: "center",
    },
    mainContentContainer: {
      paddingTop: isDesktop ? 40 : 24,
      paddingBottom: 100,
      paddingInline: 20,
      width: "100%",
      maxWidth: 1152,
      minWidth: 0,
      margin: "auto",
      backgroundColor: Colors.WHITE,
      gap: 40,
    },
    heroContainer: {
      width: "100%",
      maxWidth: 1152,
      minWidth: 0,
      margin: "auto",
      paddingInline: 20,
      gap: isDesktop ? 60 : 24,
    },
  };

  // only handles deleting events from search bar
  const deleteSearchedItem = useCallback(
    (id: string) => {
      dispatch(deleteSearchHistory(accountData.uid, "marks", id));
    },
    [accountData.uid, dispatch]
  );
  const addSearchedEvent = useCallback(
    (id: string) => {
      dispatch(addSearchHistory(accountData.uid, "marks", id));
    },
    [accountData.uid, dispatch]
  );

  useAsyncOnMount(async () => {
    const [featuredPeople, trendingPeople] = await Promise.all([
      fetchExploreFeaturedUsers(ExploreTopCreators),
      fetchExploreFeaturedUsers(ExploreTrendingCreators),
    ]);
    setFeaturedPeople(featuredPeople);
    setTrendingPeople(trendingPeople);
    setIsLoading(false);
  });

  const enterOnSearchBar = (e: any) => {
    if (e.key === "Enter" && searchTerm.trim() !== "") {
      if (selectedSearchOption === SearchOptionType.RECENT) {
        setSelectedSearchOption(SearchOptionType.EVENTS);
      }
      handleSearch(searchTerm);
    }
  };

  // Retrieve recent events searched
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    // TODO - Sort events by search date once we support showing more searches
    const existing = searchedEventsHistory.map((event) => event.id);
    const deleted = existing.filter(
      (eid) => !searchHistory.marks.includes(eid)
    );
    const added = searchHistory.marks.filter((eid) => !existing.includes(eid));
    if (deleted.length === 0 && added.length === 0) {
      return;
    }

    let prev: Event[] = [...searchedEventsHistory];
    let data: Event[] = [];
    if (deleted.length > 0) {
      prev = prev.filter((event) => !deleted.includes(event.id));
    }

    if (added.length > 0) {
      const promises = added.map((eid) => getEventData(eid));
      const results = await Promise.all(promises);
      data = results.filter((user): user is Event => {
        return user !== undefined;
      });
      prev = searchedEventsHistory.filter((event) =>
        searchHistory.marks.includes(event.id)
      );
    }

    setSearchedEventsHistory(
      data
        .concat(prev)
        .filter(
          (event) => !event.isDraft && event.visibility === Visibility.Public
        )
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchHistory.marks]);

  const renderSelectedComponent = useCallback(() => {
    switch (selectedSearchOption) {
      case SearchOptionType.RECENT:
        return (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "14px" }}
          >
            {searchedEventsHistory.length > 0 ? (
              searchedEventsHistory.map((event) => {
                return (
                  <FeaturedEventCardMobileSearch
                    event={event}
                    deleteOnClick={() => deleteSearchedItem(event.id)}
                    key={event.id}
                  />
                );
              })
            ) : (
              <EmptySearchState mainText={"No recents to show"} />
            )}
          </div>
        );
      case SearchOptionType.EVENTS:
        return (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "14px" }}
          >
            {searchedEvents.length > 0 ? (
              searchedEvents.map((event) => {
                return (
                  <FeaturedEventCardMobile
                    event={event}
                    addToSearchHistory={addSearchedEvent}
                    key={event.id}
                  />
                );
              })
            ) : (
              <EmptySearchState mainText={"No events to show"} />
            )}
          </div>
        );
      case SearchOptionType.PEOPLE:
        return (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "14px" }}
          >
            {searchedUsers.length > 0 ? (
              searchedUsers.map((user) => {
                return <RenderedUser user={user} key={user.uid} />;
              })
            ) : (
              <EmptySearchState mainText={"No people to show"} />
            )}
          </div>
        );
      // @TODO: To implement when location-based search is ready
      // case 'Places':
      //   return ...
      default:
        return null;
    }
  }, [
    selectedSearchOption,
    searchedEventsHistory,
    searchedEvents,
    searchedUsers,
    deleteSearchedItem,
    addSearchedEvent,
  ]);

  const SearchOption = (props: {
    option: SearchOptionType;
    mobile?: boolean;
  }) => {
    const { option, mobile } = props;

    return (
      <div
        style={{
          backgroundColor:
            selectedSearchOption === option
              ? Colors.BLACK
              : mobile
                ? Colors.WHITE
                : Colors.GRAY6,
          paddingBlock: "7px",
          paddingInline: "12px",
          borderRadius: "8px",
          cursor: "pointer",
        }}
        onClick={() => setSelectedSearchOption(option)}
      >
        <span
          style={{
            color:
              selectedSearchOption === option ? Colors.WHITE : Colors.BLACK,
            cursor: "pointer",
            fontSize: "15px",
            fontWeight: 500,
          }}
        >
          {option}
        </span>
      </div>
    );
  };

  const [timeoutEvent, setTimeoutEvent] = useState<
    NodeJS.Timeout | undefined
  >();

  const search = useCallback(async (text: string, target: Collection) => {
    const ids = (
      await algoliaClient.searchSingleIndex({
        indexName: target,
        searchParams: { query: text },
      })
    ).hits.map((obj: any) => obj.objectID);

    return ids;
  }, []);

  const handlePeople = useCallback(
    async (text: string) => {
      if (text.length <= 1) {
        setSearchedUsers([]);
        return;
      }
      const userIds = await search(text, Collection.USERS);
      const promises = userIds.map((uid: string) => getUserData(uid));
      Promise.all(promises).then((results) => {
        setSearchedUsers(
          results.filter((user: any): user is AccountData => user !== undefined)
        );
      });
    },
    [search]
  );

  const handleEvents = useCallback(
    async (text: string) => {
      if (text.length <= 2) {
        setSearchedEvents([]);
        return;
      }
      const eventIds = await search(text, Collection.EVENTS);
      const promises = eventIds.map((eid: string) => getEventData(eid));
      const results = await Promise.all(promises);

      setSearchedEvents(
        results.filter(
          (event: any): event is Event =>
            event !== undefined &&
            !event.isDraft &&
            event.visibility === Visibility.Public
        )
      );
    },
    [search]
  );

  const handleSearch = useCallback(
    async (text: string) => {
      setSearchTerm(text);
      if (timeoutEvent) {
        clearTimeout(timeoutEvent);
      }
      setTimeoutEvent(
        setTimeout(async () => {
          await handleEvents(text);
          await handlePeople(text);
        }, 500)
      );
    },
    [handleEvents, handlePeople, timeoutEvent]
  );

  if (isLoading) {
    return <LoadingScreen />;
  } else {
    return (
      <div style={{ position: "relative", height: "auto" }}>
        <Helmet>
          <title>Discover Creators & Brands | Markit AI</title>
          <meta
            name="description"
            content="Discover and subscribe to other creators, brands, and businesses on Markit. Get exclusive texts and updates the moment they're posted."
          />
          <link rel="canonical" href={MARKITAI + NavigationId.EXPLORE} />
        </Helmet>
        <div style={{ ...styles.gradientHeader, boxSizing: "border-box" }}>
          <TopHeader eventTheme={ThemeType.Dark} nonCreatorPage />
          <div
            className="ColumnNormal"
            style={{ ...styles.heroContainer, boxSizing: "border-box" }}
          >
            <ExploreHero />
            <div
              className="SearchBarContainer"
              style={{
                backgroundColor: isDesktop
                  ? addAlpha(Colors.GRAY11, 0.1)
                  : Colors.WHITE,
                borderRadius: isDesktop ? 100 : 10,
                borderStyle: "solid",
                borderColor: addAlpha(Colors.GRAY11, 0.2),
              }}
            >
              <div
                className="SearchBarIconAndInputContainer"
                style={{ paddingBlock: isDesktop ? 12 : 8 }}
              >
                <Icon
                  icon="ion:search"
                  height={20}
                  style={{
                    color: isDesktop ? Colors.WHITE : Colors.GRAY2,
                  }}
                />
                <input
                  className={`SearchBarInput ${
                    isDesktop ? "LightSearchBarInput" : "DarkSearchBarInput"
                  }`}
                  placeholder={"Search"}
                  onKeyDown={enterOnSearchBar}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => setDisplaySearchModal(true)}
                  value={searchTerm}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          className="ColumnNormal"
          style={{ ...styles.mainContentContainer, boxSizing: "border-box" }}
        >
          <FeaturedPeople
            featuredPeople={featuredPeople}
            title="Top Creators"
            icon={
              <Icon
                icon="ion:flame"
                height={28}
                style={{ color: Colors.RED3 }}
              />
            }
            subtext="Subscribe to the top creators and brands on Markit"
          />
          <ExploreCategories />
          <FeaturedPeople
            featuredPeople={trendingPeople}
            title="Trending"
            icon={
              <Icon
                icon="ion:trending-up"
                height={30}
                style={{ color: Colors.GREEN2 }}
              />
            }
            subtext="Subscribe to the trending creators, brands, and businesses on Markit"
          />
        </div>
        {displaySearchModal ? (
          <AnimatePresence>
            <LazyMotion features={domAnimation}>
              {isDesktopSize ? (
                <m.div
                  key="searchModalBg"
                  className="SearchBarBgDesktop HideScrollbar"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.4 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setDisplaySearchModal(false)}
                  style={{ zIndex: 10 }}
                />
              ) : (
                <div className="SearchBgMobile" />
              )}
              <m.div
                key={
                  isDesktopSize
                    ? "searchModalContainerDesktop"
                    : "searchBarContainerMobile"
                }
                className={
                  isDesktopSize
                    ? "SearchBarContainerDesktop HideScrollbar ColumnNormal"
                    : "SearchBarContainerMobile HideScrollbar ColumnNormal"
                }
                initial={
                  isDesktopSize
                    ? { height: 0, scale: 1.01 }
                    : { top: "90.5px", opacity: 0 }
                }
                animate={
                  isDesktopSize
                    ? { height: 600, scale: 1 }
                    : { top: "48px", opacity: 1 }
                }
                exit={
                  isDesktopSize
                    ? { height: 0, scale: 1.01 }
                    : { top: "90.5px", opacity: 0 }
                }
                transition={{
                  duration: isDesktopSize ? 0.4 : 0.3,
                  type: isDesktopSize ? undefined : "tween",
                }}
                style={{ zIndex: 100, boxSizing: "border-box" }}
              >
                <m.div
                  className="AlignedRowSpaced"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="SearchBarContainer" style={{ marginTop: 0 }}>
                    <div className="SearchBarIconAndInputContainer">
                      <Icon
                        icon="ion:search"
                        height={18}
                        style={{ color: Colors.BLACK }}
                      />
                      <input
                        autoFocus
                        className="SearchBarInput DarkSearchBarInput"
                        placeholder="Search Events, People, Places..."
                        onKeyDown={enterOnSearchBar}
                        onChange={(e) => handleSearch(e.target.value)}
                        value={searchTerm}
                      />
                      <Icon
                        icon="ion:close-outline"
                        height={18}
                        style={{ color: Colors.GRAY1, cursor: "pointer" }}
                        onClick={() => handleSearch("")}
                      />
                    </div>
                  </div>
                  <span
                    style={{ marginLeft: "14px", cursor: "pointer" }}
                    onClick={() => {
                      handleSearch("");
                      setDisplaySearchModal(false);
                    }}
                  >
                    Cancel
                  </span>
                </m.div>
                <m.div
                  className="SearchOptionsContainer"
                  key="searchModalContainerOptions"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <SearchOption
                    option={SearchOptionType.RECENT}
                    mobile={!isDesktopSize}
                  />
                  <SearchOption
                    option={SearchOptionType.PEOPLE}
                    mobile={!isDesktopSize}
                  />
                  <SearchOption
                    option={SearchOptionType.EVENTS}
                    mobile={!isDesktopSize}
                  />
                  {/* @TODO: Implement location-based search */}
                  {/* <SearchOption option="Places" /> */}
                </m.div>
                <m.div
                  key="searchModalContainerDivider"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <hr />
                </m.div>
                <m.div
                  key="searchModalContainerContent"
                  style={{
                    overflowY: "auto",
                    flex: 1,
                    scrollbarWidth: "none",
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {renderSelectedComponent()}
                  {isDesktopSize && <div style={{ paddingBottom: 100 }}></div>}
                </m.div>
              </m.div>
            </LazyMotion>
          </AnimatePresence>
        ) : null}
        <Footer />
      </div>
    );
  }
};
export default ExplorePage;
